import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card, Dropdown } from "react-bootstrap";
import SafetyRiunioniService from "../../../services/safety/SafetyRiunioniService";
import SafetyNomineService from "../../../services/safety/SafetyNomineService";

const EMPTY = {
    anno: "",
    stato: "PROGRAMMATA",
    data_convocazione: "",
    data_riunione: "",
    ora_riunione: "",
    luogo: "",
    partecipanti_altri: "",
    ordine_del_giorno: "",
    esito: "",
    note: "",
};

const STATI = [
    { value: "PROGRAMMATA", label: "Programmata" },
    { value: "SVOLTA", label: "Svolta" },
    { value: "ANNULLATA", label: "Annullata" },
];

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function statoBadge(stato) {
    const map = {
        PROGRAMMATA: { bg: "info", text: "dark", label: "Programmata" },
        SVOLTA:      { bg: "success", text: "light", label: "Svolta" },
        ANNULLATA:   { bg: "secondary", text: "light", label: "Annullata" },
    };
    const m = map[stato] || map.PROGRAMMATA;
    return <Badge bg={m.bg} text={m.text}>{m.label}</Badge>;
}

function displayIncaricato(n) {
    if (n.nominativo) return n.nominativo;
    if (n.lav_cognome) return `${n.lav_cognome} ${n.lav_nome || ""}`.trim();
    return "—";
}

function nominaLabel(n) {
    const ruolo = n.tipo_denominazione || n.titolo || "Figura";
    return `${ruolo}: ${displayIncaricato(n)}`;
}

function partecipantiSummary(r) {
    const figure = Array.isArray(r.partecipanti_figure) ? r.partecipanti_figure : [];
    const altri = r.partecipanti_altri_testo || r.partecipanti_altri || "";
    const altriLines = altri.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const total = figure.length + altriLines.length || (r.partecipanti_conteggio ?? 0);
    if (total <= 0 && !r.partecipanti) return <span className="text-muted">—</span>;
    return (
        <span className="small">
            <Badge bg="light" text="dark" className="border me-1">{total || "—"}</Badge>
            {figure.slice(0, 2).map((f, i) => (
                <span key={f.id_nomina || i} className="text-muted d-block d-md-inline me-md-2">
                    {f.tipo}: {f.nominativo}
                </span>
            ))}
            {figure.length > 2 && <span className="text-muted">+{figure.length - 2} figure</span>}
        </span>
    );
}

export default function RiunioniPanel({ idAzienda, numeroDipendenti = 0 }) {
    const annoCorrente = new Date().getFullYear();
    const [anno, setAnno] = useState(annoCorrente);
    const [items, setItems] = useState([]);
    const [compliance, setCompliance] = useState(null);
    const [nomineList, setNomineList] = useState([]);
    const [selectedNomine, setSelectedNomine] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingNomine, setLoadingNomine] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const parseJson = async (resp) => {
        return resp && typeof resp.json === "function" ? await resp.json() : resp;
    };

    const loadCompliance = useCallback(async () => {
        if (!idAzienda) { setCompliance(null); return; }
        try {
            const resp = await SafetyRiunioniService.compliance(idAzienda, anno);
            setCompliance(await parseJson(resp));
        } catch {
            setCompliance(null);
        }
    }, [idAzienda, anno]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyRiunioniService.byAzienda(idAzienda, anno);
            const data = await parseJson(resp);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento delle riunioni periodiche.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda, anno]);

    const loadNomine = useCallback(async () => {
        if (!idAzienda) { setNomineList([]); return; }
        setLoadingNomine(true);
        try {
            const resp = await SafetyNomineService.byAzienda(idAzienda);
            const data = await parseJson(resp);
            setNomineList(Array.isArray(data) ? data : []);
        } catch {
            setNomineList([]);
        } finally {
            setLoadingNomine(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadCompliance(); }, [loadCompliance]);
    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => {
        if (show) loadNomine();
    }, [show, loadNomine]);

    const openForm = () => {
        setForm({ ...EMPTY, anno: String(anno), stato: "SVOLTA", data_riunione: new Date().toISOString().substring(0, 10) });
        setSelectedNomine([]);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const addNomina = (n) => {
        if (!n?.id_nomina) return;
        if (selectedNomine.some(s => s.id_nomina === n.id_nomina)) return;
        setSelectedNomine(prev => [...prev, n]);
    };

    const removeNomina = (idNomina) => {
        setSelectedNomine(prev => prev.filter(n => n.id_nomina !== idNomina));
    };

    const nomineDisponibili = nomineList.filter(
        n => !selectedNomine.some(s => s.id_nomina === n.id_nomina)
    );

    const submit = async () => {
        if (!idAzienda) return;
        if (form.stato === "SVOLTA" && !form.data_riunione) {
            setError("Indica la data di svolgimento per una riunione con stato «Svolta».");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const figurePayload = selectedNomine.map(n => ({
                id_nomina: n.id_nomina,
                tipo: n.tipo_denominazione || n.titolo || "Figura",
                nominativo: displayIncaricato(n),
            }));
            const riepilogo = [
                ...selectedNomine.map(nominaLabel),
                ...(form.partecipanti_altri || "").split(/\r?\n/).map(l => l.trim()).filter(Boolean),
            ].join("\n");

            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("anno", form.anno || String(anno));
            fd.append("stato", form.stato);
            if (form.data_convocazione) fd.append("data_convocazione", form.data_convocazione);
            if (form.data_riunione)     fd.append("data_riunione", form.data_riunione);
            if (form.ora_riunione)      fd.append("ora_riunione", form.ora_riunione);
            if (form.luogo)             fd.append("luogo", form.luogo);
            if (figurePayload.length)   fd.append("partecipanti_nomine", JSON.stringify(figurePayload));
            if (form.partecipanti_altri) fd.append("partecipanti_altri", form.partecipanti_altri);
            if (riepilogo)              fd.append("partecipanti", riepilogo);
            if (form.ordine_del_giorno) fd.append("ordine_del_giorno", form.ordine_del_giorno);
            if (form.esito)             fd.append("esito", form.esito);
            if (form.note)              fd.append("note", form.note);
            if (file)                   fd.append("file", file);
            await SafetyRiunioniService.create(fd);
            setMsg("Riunione registrata.");
            setShow(false);
            setForm(EMPTY);
            setSelectedNomine([]);
            setFile(null);
            await loadItems();
            await loadCompliance();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (r) => {
        setError(null);
        try {
            await SafetyRiunioniService.download(r.id_riunione, `verbale_riunione_${r.anno}.pdf`);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il verbale.");
        }
    };

    const onDelete = async (r) => {
        if (!r?.id_riunione) return;
        if (!window.confirm("Eliminare questa riunione dal registro?")) return;
        try {
            await SafetyRiunioniService.deleteRiunione(r.id_riunione);
            await loadItems();
            await loadCompliance();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire le riunioni periodiche (art. 35).
            </BsAlert>
        );
    }

    const obbligatoria = compliance?.obbligatoria ?? (Number(numeroDipendenti) >= 15);
    const alertVariant = compliance?.stato_adempimento === "OK" ? "success"
        : compliance?.stato_adempimento === "NON_OBBLIGATORIA" ? "info"
        : compliance?.stato_adempimento === "SENZA_VERBALE" ? "warning" : "danger";

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                    <h6 className="mb-1 fw-semibold">
                        <i className="bi bi-calendar-check me-1 text-primary" />
                        Riunione periodica (art. 35)
                    </h6>
                    <div className="text-muted small">
                        Obbligatoria per aziende con ≥ 15 lavoratori · una volta all'anno
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Form.Select size="sm" style={{ width: 100 }} value={anno} onChange={e => setAnno(Number(e.target.value))}>
                        {[annoCorrente - 2, annoCorrente - 1, annoCorrente, annoCorrente + 1].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </Form.Select>
                    <Button size="sm" variant="primary" onClick={openForm}>
                        <i className="bi bi-plus-lg me-1" />Registra riunione
                    </Button>
                </div>
            </div>

            {compliance && (
                <BsAlert variant={alertVariant} className="py-2 small mb-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                            <strong>Adempimento {anno}:</strong>{" "}
                            {compliance.stato_adempimento === "OK" && "Riunione svolta con verbale caricato."}
                            {compliance.stato_adempimento === "MANCANTE" && "Riunione obbligatoria non ancora svolta."}
                            {compliance.stato_adempimento === "SENZA_VERBALE" && "Riunione svolta — caricare il verbale."}
                            {compliance.stato_adempimento === "NON_OBBLIGATORIA" && `Non obbligatoria (${compliance.numero_dipendenti ?? numeroDipendenti} dipendenti, soglia ${compliance.soglia_dipendenti ?? 15}).`}
                        </div>
                        <Badge bg={obbligatoria ? "primary" : "secondary"}>
                            {compliance.numero_dipendenti ?? numeroDipendenti} dip.
                        </Badge>
                    </div>
                    {compliance.alert && <div className="mt-1">{compliance.alert}</div>}
                </BsAlert>
            )}

            {error && <BsAlert variant="danger" className="py-2 small" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <Card className="mb-3 border-primary">
                    <Card.Header className="py-2 bg-light fw-semibold small">Nuova riunione / verbale</Card.Header>
                    <Card.Body className="py-3">
                        <Row className="g-2 mb-2">
                            <Col md={2}>
                                <Form.Label className="small">Anno</Form.Label>
                                <Form.Control size="sm" type="number" value={form.anno} onChange={e => setForm(f => ({ ...f, anno: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small">Stato</Form.Label>
                                <Form.Select size="sm" value={form.stato} onChange={e => setForm(f => ({ ...f, stato: e.target.value }))}>
                                    {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small">Data convocazione</Form.Label>
                                <Form.Control size="sm" type="date" value={form.data_convocazione} onChange={e => setForm(f => ({ ...f, data_convocazione: e.target.value }))} />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small">Data riunione</Form.Label>
                                <Form.Control size="sm" type="date" value={form.data_riunione} onChange={e => setForm(f => ({ ...f, data_riunione: e.target.value }))} />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small">Ora</Form.Label>
                                <Form.Control size="sm" type="time" value={form.ora_riunione} onChange={e => setForm(f => ({ ...f, ora_riunione: e.target.value }))} />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small">Luogo</Form.Label>
                                <Form.Control size="sm" value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Sede, sala riunioni…" />
                            </Col>

                            <Col md={12}>
                                <div className="border rounded p-2 bg-white">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                        <Form.Label className="small mb-0 fw-semibold">
                                            <i className="bi bi-person-badge me-1" />
                                            Partecipanti — figure censite
                                        </Form.Label>
                                        <Dropdown>
                                            <Dropdown.Toggle size="sm" variant="outline-primary" disabled={loadingNomine || nomineDisponibili.length === 0}>
                                                {loadingNomine
                                                    ? <Spinner animation="border" size="sm" />
                                                    : <><i className="bi bi-plus-lg me-1" />Aggiungi figura</>}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu style={{ maxHeight: 280, overflowY: "auto" }}>
                                                {nomineDisponibili.length === 0 ? (
                                                    <Dropdown.Item disabled>
                                                        {nomineList.length === 0
                                                            ? "Nessuna figura in tab Nomine"
                                                            : "Tutte le figure già aggiunte"}
                                                    </Dropdown.Item>
                                                ) : nomineDisponibili.map(n => (
                                                    <Dropdown.Item key={n.id_nomina} onClick={() => addNomina(n)}>
                                                        <div className="small fw-semibold">{n.tipo_denominazione || n.titolo || "Figura"}</div>
                                                        <div className="text-muted small">{displayIncaricato(n)}</div>
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    {nomineList.length === 0 && !loadingNomine && (
                                        <div className="text-muted small mb-2">
                                            Registra prima le figure nel tab <strong>Nomine</strong>, poi aggiungile qui con un clic.
                                        </div>
                                    )}
                                    {selectedNomine.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                            {selectedNomine.map(n => (
                                                <Badge
                                                    key={n.id_nomina}
                                                    bg="primary"
                                                    className="d-inline-flex align-items-center gap-1 py-2 px-2"
                                                    style={{ fontWeight: 400 }}
                                                >
                                                    <span className="small">{nominaLabel(n)}</span>
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white ms-1"
                                                        style={{ fontSize: "0.55rem" }}
                                                        aria-label="Rimuovi"
                                                        onClick={() => removeNomina(n.id_nomina)}
                                                    />
                                                </Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted small mb-2">Nessuna figura selezionata.</div>
                                    )}
                                    <Form.Label className="small text-muted">Altri partecipanti (testo libero)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        value={form.partecipanti_altri}
                                        onChange={e => setForm(f => ({ ...f, partecipanti_altri: e.target.value }))}
                                        placeholder="Es. Datore di lavoro, delegato, consulenti non censiti — un nominativo per riga"
                                    />
                                </div>
                            </Col>

                            <Col md={12}>
                                <div className="border rounded p-2 bg-light">
                                    <Form.Label className="small fw-semibold mb-1">
                                        <i className="bi bi-paperclip me-1" />
                                        Allegato verbale
                                    </Form.Label>
                                    <div className="text-muted small mb-2">
                                        Facoltativo in fase di convocazione; consigliato (e richiesto per l'adempimento completo) una volta svolta la riunione.
                                    </div>
                                    <Form.Control
                                        size="sm"
                                        type="file"
                                        accept=".pdf,.doc,.docx,.odt"
                                        onChange={e => setFile(e.target.files?.[0] || null)}
                                    />
                                    {file && (
                                        <div className="small text-success mt-1">
                                            <i className="bi bi-file-earmark-check me-1" />
                                            {file.name} ({Math.round(file.size / 1024)} KB)
                                        </div>
                                    )}
                                </div>
                            </Col>

                            <Col md={12}>
                                <Form.Label className="small">Ordine del giorno</Form.Label>
                                <Form.Control as="textarea" rows={2} size="sm" value={form.ordine_del_giorno} onChange={e => setForm(f => ({ ...f, ordine_del_giorno: e.target.value }))} />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small">Esito / deliberazioni</Form.Label>
                                <Form.Control as="textarea" rows={2} size="sm" value={form.esito} onChange={e => setForm(f => ({ ...f, esito: e.target.value }))} />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="small">Note</Form.Label>
                                <Form.Control size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                            </Col>
                        </Row>
                        <div className="d-flex gap-2">
                            <Button size="sm" variant="primary" disabled={saving} onClick={submit}>
                                {saving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-check-lg me-1" />Salva</>}
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)}>Annulla</Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {loading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
            ) : items.length === 0 ? (
                <BsAlert variant="light" className="py-2 small border text-muted mb-0">
                    Nessuna riunione registrata per l'anno {anno}.
                    {obbligatoria && anno === annoCorrente && " Registra la convocazione o il verbale della riunione periodica obbligatoria."}
                </BsAlert>
            ) : (
                <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Anno</th>
                            <th>Stato</th>
                            <th>Convocazione</th>
                            <th>Svolgimento</th>
                            <th>Partecipanti</th>
                            <th>Verbale</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(r => (
                            <tr key={r.id_riunione}>
                                <td>{r.anno}</td>
                                <td>{statoBadge(r.stato)}</td>
                                <td>{fmtDate(r.data_convocazione)}</td>
                                <td>
                                    {fmtDate(r.data_riunione)}
                                    {r.ora_riunione ? ` ${String(r.ora_riunione).substring(0, 5)}` : ""}
                                    {r.luogo && <div className="text-muted small">{r.luogo}</div>}
                                </td>
                                <td>{partecipantiSummary(r)}</td>
                                <td>
                                    {r.verbale_presente
                                        ? <Badge bg="success">Allegato</Badge>
                                        : r.stato === "SVOLTA"
                                            ? <Badge bg="warning" text="dark">Da allegare</Badge>
                                            : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-end text-nowrap">
                                    {r.path_file && (
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => onDownload(r)} title="Scarica verbale">
                                            <i className="bi bi-download" />
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(r)} title="Elimina">
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
